import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ChevronUp, ChevronDown, Plus, Trash2, Save, AlertTriangle,
  Mail, GripVertical, Edit, Shield, DollarSign, ArrowRight, Eye
} from "lucide-react";
import MarketSmtpPanel from "./MarketSmtpPanel";

export default function MarketConfig() {
  const { user } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState("");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Dialog state
  const [roleDialog, setRoleDialog] = useState({ open: false, role: null });
  const [emailDialog, setEmailDialog] = useState({ open: false, roleId: null, email: null });
  const [tierDialog, setTierDialog] = useState({ open: false, tier: null });
  const [treasurerDialog, setTreasurerDialog] = useState({ open: false, email: null });
  const [technicianDialog, setTechnicianDialog] = useState({ open: false, email: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: "", id: "", label: "" });
  const [previewCost, setPreviewCost] = useState("");
  const [newRestaurant, setNewRestaurant] = useState("");
  const [editingRestIdx, setEditingRestIdx] = useState(-1);
  const [editingRestName, setEditingRestName] = useState("");

  const fetchMarkets = useCallback(async () => {
    try {
      const res = await api.get("/units");
      const active = res.data.filter((u) => u.active !== false);
      setMarkets(active);
      if (active.length > 0 && !selectedMarket) {
        setSelectedMarket(active[0].name);
      }
    } catch {
      toast.error("Failed to load markets");
    }
  }, []);

  const fetchConfig = useCallback(async (marketId) => {
    if (!marketId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/market-config/${encodeURIComponent(marketId)}`);
      setConfig(res.data);
      setDirty(false);
    } catch {
      toast.error("Failed to load market configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMarkets(); }, []);
  useEffect(() => { if (selectedMarket) fetchConfig(selectedMarket); }, [selectedMarket]);

  const updateConfig = (updater) => {
    setConfig((prev) => {
      const next = updater(prev);
      return next;
    });
    setDirty(true);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/market-config/${encodeURIComponent(selectedMarket)}`, {
        approval_chain: config.approval_chain,
        cost_tiers: config.cost_tiers,
        treasurer_emails: config.treasurer_emails,
        technician_emails: config.technician_emails || [],
        restaurants: config.restaurants || [],
      });
      toast.success(`${selectedMarket} configuration saved`);
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  // ========== Role CRUD ==========
  const saveRole = (data) => {
    updateConfig((c) => {
      const chain = [...c.approval_chain];
      const idx = chain.findIndex((r) => r.id === data.id);
      if (idx >= 0) {
        chain[idx] = { ...chain[idx], ...data };
      } else {
        chain.push({ ...data, order: chain.length, emails: [] });
      }
      return { ...c, approval_chain: chain };
    });
    setRoleDialog({ open: false, role: null });
  };

  const moveRole = (roleId, dir) => {
    updateConfig((c) => {
      const chain = [...c.approval_chain].sort((a, b) => a.order - b.order);
      const idx = chain.findIndex((r) => r.id === roleId);
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= chain.length) return c;
      [chain[idx], chain[newIdx]] = [chain[newIdx], chain[idx]];
      chain.forEach((r, i) => (r.order = i));
      return { ...c, approval_chain: chain };
    });
  };

  const toggleRole = (roleId) => {
    updateConfig((c) => ({
      ...c,
      approval_chain: c.approval_chain.map((r) =>
        r.id === roleId ? { ...r, active: !r.active } : r
      ),
    }));
  };

  const deleteRole = (roleId) => {
    updateConfig((c) => ({
      ...c,
      approval_chain: c.approval_chain
        .filter((r) => r.id !== roleId)
        .map((r, i) => ({ ...r, order: i })),
      cost_tiers: c.cost_tiers.map((t) => ({
        ...t,
        approval_sequence: t.approval_sequence.filter((id) => id !== roleId),
      })),
    }));
    setDeleteConfirm({ open: false, type: "", id: "", label: "" });
  };

  // ========== Role Email CRUD ==========
  const saveRoleEmail = (roleId, data) => {
    updateConfig((c) => ({
      ...c,
      approval_chain: c.approval_chain.map((r) => {
        if (r.id !== roleId) return r;
        const emails = [...r.emails];
        const idx = emails.findIndex((e) => e.id === data.id);
        if (idx >= 0) emails[idx] = data;
        else emails.push(data);
        return { ...r, emails };
      }),
    }));
    setEmailDialog({ open: false, roleId: null, email: null });
  };

  const deleteRoleEmail = (roleId, emailId) => {
    updateConfig((c) => ({
      ...c,
      approval_chain: c.approval_chain.map((r) =>
        r.id === roleId ? { ...r, emails: r.emails.filter((e) => e.id !== emailId) } : r
      ),
    }));
  };

  const toggleRoleEmail = (roleId, emailId) => {
    updateConfig((c) => ({
      ...c,
      approval_chain: c.approval_chain.map((r) =>
        r.id === roleId
          ? { ...r, emails: r.emails.map((e) => (e.id === emailId ? { ...e, active: !e.active } : e)) }
          : r
      ),
    }));
  };

  // ========== Cost Tier CRUD ==========
  const saveTier = (data) => {
    updateConfig((c) => {
      const tiers = [...c.cost_tiers];
      const idx = tiers.findIndex((t) => t.id === data.id);
      if (idx >= 0) tiers[idx] = { ...tiers[idx], ...data };
      else tiers.push({ ...data, order: tiers.length });
      return { ...c, cost_tiers: tiers };
    });
    setTierDialog({ open: false, tier: null });
  };

  const deleteTier = (tierId) => {
    updateConfig((c) => ({
      ...c,
      cost_tiers: c.cost_tiers.filter((t) => t.id !== tierId).map((t, i) => ({ ...t, order: i })),
    }));
    setDeleteConfirm({ open: false, type: "", id: "", label: "" });
  };

  const moveTier = (tierId, dir) => {
    updateConfig((c) => {
      const tiers = [...c.cost_tiers].sort((a, b) => a.order - b.order);
      const idx = tiers.findIndex((t) => t.id === tierId);
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= tiers.length) return c;
      [tiers[idx], tiers[newIdx]] = [tiers[newIdx], tiers[idx]];
      tiers.forEach((t, i) => (t.order = i));
      return { ...c, cost_tiers: tiers };
    });
  };

  // ========== Treasurer Email CRUD ==========
  const saveTreasurerEmail = (data) => {
    updateConfig((c) => {
      const list = [...(c.treasurer_emails || [])];
      const idx = list.findIndex((e) => e.id === data.id);
      if (idx >= 0) list[idx] = data;
      else list.push(data);
      return { ...c, treasurer_emails: list };
    });
    setTreasurerDialog({ open: false, email: null });
  };

  const deleteTreasurerEmail = (emailId) => {
    updateConfig((c) => ({
      ...c,
      treasurer_emails: (c.treasurer_emails || []).filter((e) => e.id !== emailId),
    }));
  };

  // ========== Technician Email CRUD ==========
  const saveTechnicianEmail = (data) => {
    updateConfig((c) => {
      const list = [...(c.technician_emails || [])];
      const idx = list.findIndex((e) => e.id === data.id);
      if (idx >= 0) list[idx] = data;
      else list.push(data);
      return { ...c, technician_emails: list };
    });
    setTechnicianDialog({ open: false, email: null });
  };

  const deleteTechnicianEmail = (emailId) => {
    updateConfig((c) => ({
      ...c,
      technician_emails: (c.technician_emails || []).filter((e) => e.id !== emailId),
    }));
  };

  // ========== Helpers ==========
  const genId = () => crypto.randomUUID();
  const sortedChain = config ? [...config.approval_chain].sort((a, b) => a.order - b.order) : [];
  const sortedTiers = config ? [...config.cost_tiers].sort((a, b) => a.order - b.order) : [];
  const activeRoles = sortedChain.filter((r) => r.active);

  const getRoleName = (roleId) => {
    const r = config?.approval_chain.find((r) => r.id === roleId);
    return r ? r.display_label || r.role_name : "Unknown";
  };

  const getWarnings = () => {
    if (!config) return [];
    const warnings = [];
    config.approval_chain.forEach((role) => {
      const activeEmails = role.emails.filter((e) => e.active && e.email);
      if (role.active && activeEmails.length === 0) {
        warnings.push(`Role "${role.display_label || role.role_name}" has no active email addresses`);
      }
    });
    config.cost_tiers.forEach((tier) => {
      if (!tier.approval_sequence || tier.approval_sequence.length === 0) {
        warnings.push(`Tier "${tier.name}" has no linked approval roles`);
      }
      tier.approval_sequence?.forEach((rid) => {
        const role = config.approval_chain.find((r) => r.id === rid);
        if (role && role.active) {
          const activeEmails = role.emails.filter((e) => e.active && e.email);
          if (activeEmails.length === 0) {
            warnings.push(`Tier "${tier.name}" links to "${role.display_label || role.role_name}" which has no active emails`);
          }
        }
      });
    });
    const activeTechEmails = (config.technician_emails || []).filter((e) => e.active && e.email);
    if (activeTechEmails.length === 0) {
      warnings.push("No active technician emails configured — escalated tickets won't trigger notifications");
    }
    return [...new Set(warnings)];
  };

  const getPreviewFlow = (costVal) => {
    if (!config || !costVal) return null;
    const cost = parseFloat(costVal);
    if (isNaN(cost)) return null;
    for (const tier of sortedTiers) {
      const from = tier.from_amount;
      const to = tier.up_to_amount;
      if (to === null || to === undefined) {
        if (cost >= from) return { tier, roles: tier.approval_sequence.map(getRoleName) };
      } else if (cost >= from && cost <= to) {
        return { tier, roles: tier.approval_sequence.map(getRoleName) };
      }
    }
    return null;
  };

  const warnings = getWarnings();
  const preview = getPreviewFlow(previewCost);

  if (!config && !loading) return null;

  return (
    <div className="space-y-6">
      {/* Market Selector */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="market-selector">
        {markets.map((m) => (
          <Button
            key={m.name}
            variant={selectedMarket === m.name ? "default" : "outline"}
            onClick={() => {
              if (dirty && !window.confirm("You have unsaved changes. Switch market?")) return;
              setSelectedMarket(m.name);
            }}
            className={selectedMarket === m.name ? "bg-indigo-600 hover:bg-indigo-700" : ""}
            data-testid={`market-btn-${m.name.replace(/\s/g, "-").toLowerCase()}`}
          >
            {m.name}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : config ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT: Approval Chain */}
            <div className="space-y-4">
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-indigo-600" /> Approval Chain
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => setRoleDialog({ open: true, role: null })}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="add-role-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Role
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {sortedChain.length === 0 ? (
                    <p className="text-sm text-gray-400 italic py-4 text-center">No roles configured. Add a role to start building the approval chain.</p>
                  ) : (
                    sortedChain.map((role, idx) => {
                      const activeEmails = role.emails.filter((e) => e.active && e.email);
                      const noEmails = role.active && activeEmails.length === 0;
                      return (
                        <div
                          key={role.id}
                          className={`border rounded-lg p-3 ${!role.active ? "opacity-50 bg-gray-50" : noEmails ? "border-amber-300 bg-amber-50/30" : "border-gray-200 bg-white"}`}
                          data-testid={`role-card-${role.id}`}
                        >
                          {/* Role header */}
                          <div className="flex items-center gap-2 mb-2">
                            <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                            <span className="text-xs font-bold text-indigo-600 bg-indigo-50 rounded px-1.5 py-0.5">{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-sm text-gray-900">{role.display_label || role.role_name}</span>
                              {role.display_label && role.display_label !== role.role_name && (
                                <span className="text-xs text-gray-400 ml-1">({role.role_name})</span>
                              )}
                            </div>
                            {noEmails && <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" title="No active emails" />}
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <Switch
                                checked={role.active}
                                onCheckedChange={() => toggleRole(role.id)}
                                className="scale-75"
                                data-testid={`toggle-role-${role.id}`}
                              />
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveRole(role.id, -1)} disabled={idx === 0}>
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveRole(role.id, 1)} disabled={idx === sortedChain.length - 1}>
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setRoleDialog({ open: true, role })}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                onClick={() => setDeleteConfirm({ open: true, type: "role", id: role.id, label: role.display_label || role.role_name })}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                          {/* Emails list */}
                          <div className="ml-6 space-y-1.5">
                            {role.emails.map((em) => (
                              <div key={em.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1.5">
                                <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <span className={`flex-1 truncate ${!em.active ? "line-through text-gray-400" : "text-gray-700"}`}>
                                  {em.name ? `${em.name} ` : ""}<span className="text-gray-500">&lt;{em.email}&gt;</span>
                                </span>
                                <Switch
                                  checked={em.active}
                                  onCheckedChange={() => toggleRoleEmail(role.id, em.id)}
                                  className="scale-[0.6]"
                                />
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setEmailDialog({ open: true, roleId: role.id, email: em })}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400" onClick={() => deleteRoleEmail(role.id, em.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs text-indigo-600"
                              onClick={() => setEmailDialog({ open: true, roleId: role.id, email: null })}
                              data-testid={`add-email-role-${role.id}`}
                            >
                              <Plus className="w-3 h-3 mr-1" /> Add Email
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* Treasurer Section */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" /> Treasurer (Payment Notifications)
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTreasurerDialog({ open: true, email: null })}
                    data-testid="add-treasurer-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Email
                  </Button>
                </CardHeader>
                <CardContent>
                  {(!config.treasurer_emails || config.treasurer_emails.length === 0) ? (
                    <p className="text-sm text-gray-400 italic">No treasurer emails configured</p>
                  ) : (
                    <div className="space-y-1.5">
                      {config.treasurer_emails.map((em) => (
                        <div key={em.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1.5">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span className={`flex-1 truncate ${!em.active ? "line-through text-gray-400" : "text-gray-700"}`}>
                            {em.name ? `${em.name} ` : ""}<span className="text-gray-500">&lt;{em.email}&gt;</span>
                          </span>
                          <Switch
                            checked={em.active}
                            onCheckedChange={() => {
                              updateConfig((c) => ({
                                ...c,
                                treasurer_emails: c.treasurer_emails.map((e) =>
                                  e.id === em.id ? { ...e, active: !e.active } : e
                                ),
                              }));
                            }}
                            className="scale-[0.6]"
                          />
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setTreasurerDialog({ open: true, email: em })}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400" onClick={() => deleteTreasurerEmail(em.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Technician Section */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-600" /> Technician (Escalation Target)
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTechnicianDialog({ open: true, email: null })}
                    data-testid="add-technician-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Email
                  </Button>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500 mb-3">When troubleshooting fails, the ticket is escalated directly to these technicians.</p>
                  {(!config.technician_emails || config.technician_emails.length === 0) ? (
                    <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg p-3">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span>No technician emails configured — escalations won't trigger notifications</span>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {config.technician_emails.map((em) => (
                        <div key={em.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1.5">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span className={`flex-1 truncate ${!em.active ? "line-through text-gray-400" : "text-gray-700"}`}>
                            {em.name ? `${em.name} ` : ""}<span className="text-gray-500">&lt;{em.email}&gt;</span>
                          </span>
                          <Switch
                            checked={em.active}
                            onCheckedChange={() => {
                              updateConfig((c) => ({
                                ...c,
                                technician_emails: c.technician_emails.map((e) =>
                                  e.id === em.id ? { ...e, active: !e.active } : e
                                ),
                              }));
                            }}
                            className="scale-[0.6]"
                          />
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setTechnicianDialog({ open: true, email: em })}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400" onClick={() => deleteTechnicianEmail(em.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Per-market SMTP override */}
              <MarketSmtpPanel marketId={selectedMarket} />

              {/* Restaurants Section */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-indigo-600" /> Restaurants ({(config.restaurants || []).length}/25)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500 mb-3">Store locations available for ticket creation in this market.</p>
                  <div className="space-y-1.5 mb-3">
                    {(config.restaurants || []).map((name, idx) => (
                      <div key={`${idx}-${name}`} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-2 py-1.5">
                        {editingRestIdx === idx ? (
                          <>
                            <Input
                              value={editingRestName}
                              onChange={(e) => setEditingRestName(e.target.value)}
                              className="h-7 text-sm flex-1"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  if (editingRestName.trim()) {
                                    updateConfig((c) => {
                                      const list = [...(c.restaurants || [])];
                                      list[idx] = editingRestName.trim();
                                      return { ...c, restaurants: list };
                                    });
                                  }
                                  setEditingRestIdx(-1);
                                } else if (e.key === "Escape") {
                                  setEditingRestIdx(-1);
                                }
                              }}
                              data-testid={`edit-restaurant-input-${idx}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-green-600"
                              onClick={() => {
                                if (editingRestName.trim()) {
                                  updateConfig((c) => {
                                    const list = [...(c.restaurants || [])];
                                    list[idx] = editingRestName.trim();
                                    return { ...c, restaurants: list };
                                  });
                                }
                                setEditingRestIdx(-1);
                              }}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-gray-700">{name}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => { setEditingRestIdx(idx); setEditingRestName(name); }}
                              data-testid={`edit-restaurant-${idx}`}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-red-400"
                              onClick={() => updateConfig((c) => ({ ...c, restaurants: (c.restaurants || []).filter((_, i) => i !== idx) }))}
                              data-testid={`delete-restaurant-${idx}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  {(config.restaurants || []).length < 25 ? (
                    <div className="flex gap-2">
                      <Input
                        value={newRestaurant}
                        onChange={(e) => setNewRestaurant(e.target.value)}
                        placeholder="Add restaurant name..."
                        className="flex-1 h-8 text-sm"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && newRestaurant.trim()) {
                            updateConfig((c) => ({ ...c, restaurants: [...(c.restaurants || []), newRestaurant.trim()] }));
                            setNewRestaurant("");
                          }
                        }}
                        data-testid="new-restaurant-input"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        disabled={!newRestaurant.trim()}
                        onClick={() => {
                          if (newRestaurant.trim()) {
                            updateConfig((c) => ({ ...c, restaurants: [...(c.restaurants || []), newRestaurant.trim()] }));
                            setNewRestaurant("");
                          }
                        }}
                        data-testid="add-restaurant-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600">Maximum 25 restaurants reached</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* RIGHT: Cost Thresholds + Live Preview */}
            <div className="space-y-4">
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-indigo-600" /> Cost Thresholds
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => setTierDialog({ open: true, tier: null })}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="add-tier-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Tier
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {sortedTiers.length === 0 ? (
                    <p className="text-sm text-gray-400 italic py-4 text-center">No cost tiers configured.</p>
                  ) : (
                    sortedTiers.map((tier, idx) => {
                      const hasWarning = !tier.approval_sequence || tier.approval_sequence.length === 0;
                      return (
                        <div
                          key={tier.id}
                          className={`border rounded-lg p-3 ${hasWarning ? "border-amber-300 bg-amber-50/30" : "border-gray-200"}`}
                          data-testid={`tier-card-${tier.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-900">{tier.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                ${tier.from_amount.toFixed(2)}
                                {tier.up_to_amount != null ? ` - $${tier.up_to_amount.toFixed(2)}` : " and above"}
                              </div>
                            </div>
                            {hasWarning && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                            <div className="flex gap-0.5">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveTier(tier.id, -1)} disabled={idx === 0}>
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveTier(tier.id, 1)} disabled={idx === sortedTiers.length - 1}>
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setTierDialog({ open: true, tier })}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-500"
                                onClick={() => setDeleteConfirm({ open: true, type: "tier", id: tier.id, label: tier.name })}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                          {/* Approval sequence */}
                          <div className="mt-2 flex flex-wrap items-center gap-1">
                            <span className="text-xs text-gray-400">Flow:</span>
                            {(tier.approval_sequence || []).map((rid, i) => (
                              <span key={rid} className="flex items-center">
                                {i > 0 && <ArrowRight className="w-3 h-3 text-gray-300 mx-0.5" />}
                                <Badge variant="outline" className="text-xs py-0 px-1.5">{getRoleName(rid)}</Badge>
                              </span>
                            ))}
                            {(!tier.approval_sequence || tier.approval_sequence.length === 0) && (
                              <span className="text-xs text-amber-600 italic">No approval roles linked</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* Live Preview */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-indigo-600" /> Live Approval Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Test amount: $</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={previewCost}
                        onChange={(e) => setPreviewCost(e.target.value)}
                        placeholder="e.g. 75"
                        className="w-32"
                        data-testid="preview-cost-input"
                      />
                    </div>
                    {preview ? (
                      <div className="p-3 bg-indigo-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">
                          Tier: <span className="font-medium text-gray-700">{preview.tier.name}</span>
                        </p>
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="text-xs text-gray-500">Ticket (${ parseFloat(previewCost).toFixed(2)})</span>
                          {preview.roles.map((name, i) => (
                            <span key={`${name}-${i}`} className="flex items-center">
                              <ArrowRight className="w-3 h-3 text-indigo-400 mx-1" />
                              <Badge className="bg-indigo-100 text-indigo-700 text-xs py-0">{name}</Badge>
                            </span>
                          ))}
                          <ArrowRight className="w-3 h-3 text-green-500 mx-1" />
                          <Badge className="bg-green-100 text-green-700 text-xs py-0">Approved</Badge>
                        </div>
                      </div>
                    ) : previewCost ? (
                      <p className="text-xs text-amber-600 p-3 bg-amber-50 rounded-lg">No matching tier found for ${parseFloat(previewCost || 0).toFixed(2)}</p>
                    ) : (
                      <p className="text-xs text-gray-400 italic">Enter a cost to see the approval flow</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="config-warnings">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-sm text-amber-800">Configuration Warnings</span>
              </div>
              <ul className="space-y-1">
                {warnings.map((w, i) => (
                  <li key={`${w}-${i}`} className="text-xs text-amber-700 pl-6">- {w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Save Button */}
          <div className="flex items-center justify-between pt-2">
            <div>
              {dirty && <span className="text-sm text-amber-600">Unsaved changes</span>}
            </div>
            <Button
              onClick={saveConfig}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 px-8"
              data-testid="save-market-config-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : `Save ${selectedMarket} Configuration`}
            </Button>
          </div>
        </>
      ) : null}

      {/* =================== DIALOGS =================== */}

      {/* Role Dialog */}
      <RoleDialog
        open={roleDialog.open}
        role={roleDialog.role}
        onClose={() => setRoleDialog({ open: false, role: null })}
        onSave={saveRole}
        genId={genId}
      />

      {/* Email Dialog */}
      <EmailDialog
        open={emailDialog.open}
        email={emailDialog.email}
        onClose={() => setEmailDialog({ open: false, roleId: null, email: null })}
        onSave={(data) => saveRoleEmail(emailDialog.roleId, data)}
        genId={genId}
      />

      {/* Tier Dialog */}
      <TierDialog
        open={tierDialog.open}
        tier={tierDialog.tier}
        activeRoles={activeRoles}
        onClose={() => setTierDialog({ open: false, tier: null })}
        onSave={saveTier}
        genId={genId}
      />

      {/* Treasurer Email Dialog */}
      <EmailDialog
        open={treasurerDialog.open}
        email={treasurerDialog.email}
        onClose={() => setTreasurerDialog({ open: false, email: null })}
        onSave={saveTreasurerEmail}
        genId={genId}
        title="Treasurer Email"
      />

      {/* Technician Email Dialog */}
      <EmailDialog
        open={technicianDialog.open}
        email={technicianDialog.email}
        onClose={() => setTechnicianDialog({ open: false, email: null })}
        onSave={saveTechnicianEmail}
        genId={genId}
        title="Technician Email"
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(o) => !o && setDeleteConfirm({ open: false, type: "", id: "", label: "" })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteConfirm.type === "role" ? "Role" : "Tier"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm.label}"?{" "}
              {deleteConfirm.type === "role" && "This will also remove it from any cost tiers."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteConfirm.type === "role" ? deleteRole(deleteConfirm.id) : deleteTier(deleteConfirm.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =================== SUB COMPONENTS ===================

function RoleDialog({ open, role, onClose, onSave, genId }) {
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (open) {
      setName(role?.role_name || "");
      setLabel(role?.display_label || "");
    }
  }, [open, role]);

  const handleSave = () => {
    if (!name.trim()) { toast.error("Role name is required"); return; }
    onSave({
      id: role?.id || genId(),
      role_name: name.trim(),
      display_label: label.trim() || name.trim(),
      order: role?.order ?? 999,
      active: role?.active ?? true,
      emails: role?.emails || [],
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{role ? "Edit Role" : "Add Role"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Role Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. District Manager" data-testid="role-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Display Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. District Manager" data-testid="role-label-input" />
            <p className="text-xs text-gray-400">Used in emails and the UI. Defaults to role name.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-role-dialog-btn">
            {role ? "Update" : "Add Role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailDialog({ open, email, onClose, onSave, genId, title = "Approver Email" }) {
  const [addr, setAddr] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) {
      setAddr(email?.email || "");
      setName(email?.name || "");
    }
  }, [open, email]);

  const handleSave = () => {
    if (!addr.trim()) { toast.error("Email is required"); return; }
    onSave({ id: email?.id || genId(), email: addr.trim(), name: name.trim(), active: email?.active ?? true });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{email ? `Edit ${title}` : `Add ${title}`}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Email Address *</Label>
            <Input type="email" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="approver@example.com" data-testid="email-addr-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Display Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith - District Manager" data-testid="email-name-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-email-dialog-btn">{email ? "Update" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TierDialog({ open, tier, activeRoles, onClose, onSave, genId }) {
  const [name, setName] = useState("");
  const [fromAmt, setFromAmt] = useState("");
  const [toAmt, setToAmt] = useState("");
  const [noUpperLimit, setNoUpperLimit] = useState(false);
  const [sequence, setSequence] = useState([]);
  const [addingRole, setAddingRole] = useState("");

  useEffect(() => {
    if (open) {
      setName(tier?.name || "");
      setFromAmt(tier?.from_amount?.toString() || "0");
      setToAmt(tier?.up_to_amount?.toString() || "");
      setNoUpperLimit(tier?.up_to_amount == null && !!tier);
      setSequence(tier?.approval_sequence || []);
      setAddingRole("");
    }
  }, [open, tier]);

  const handleSave = () => {
    if (!name.trim()) { toast.error("Tier name is required"); return; }
    const from = parseFloat(fromAmt);
    const to = noUpperLimit ? null : parseFloat(toAmt);
    if (isNaN(from)) { toast.error("From amount is required"); return; }
    if (!noUpperLimit && (isNaN(to) || to <= from)) { toast.error("Up-to amount must be greater than from"); return; }
    onSave({
      id: tier?.id || genId(),
      name: name.trim(),
      from_amount: from,
      up_to_amount: to,
      approval_sequence: sequence,
      order: tier?.order ?? 999,
    });
  };

  const addRoleToSeq = () => {
    if (addingRole && !sequence.includes(addingRole)) {
      setSequence((s) => [...s, addingRole]);
      setAddingRole("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{tier ? "Edit Cost Tier" : "Add Cost Tier"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Tier Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Small Repairs" data-testid="tier-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>From ($)</Label>
              <Input type="number" step="0.01" min="0" value={fromAmt} onChange={(e) => setFromAmt(e.target.value)} data-testid="tier-from-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Up To ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={toAmt}
                onChange={(e) => setToAmt(e.target.value)}
                disabled={noUpperLimit}
                placeholder={noUpperLimit ? "No limit" : ""}
                data-testid="tier-to-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={noUpperLimit} onCheckedChange={(v) => { setNoUpperLimit(v); if (v) setToAmt(""); }} />
            <Label className="text-sm text-gray-600">No upper limit ("Over $X")</Label>
          </div>
          <div className="space-y-1.5">
            <Label>Approval Sequence</Label>
            <div className="space-y-1.5">
              {sequence.map((rid, i) => {
                const role = activeRoles.find((r) => r.id === rid);
                return (
                  <div key={rid} className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1">
                    <span className="text-xs font-bold text-indigo-600">{i + 1}</span>
                    <span className="text-sm flex-1">{role ? role.display_label || role.role_name : "Unknown"}</span>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400" onClick={() => setSequence((s) => s.filter((_, j) => j !== i))}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
              <div className="flex gap-2">
                <Select value={addingRole} onValueChange={setAddingRole}>
                  <SelectTrigger className="flex-1 h-8 text-sm" data-testid="tier-add-role-select">
                    <SelectValue placeholder="Add role to sequence..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeRoles
                      .filter((r) => !sequence.includes(r.id))
                      .map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.display_label || r.role_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-8" onClick={addRoleToSeq} disabled={!addingRole}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-tier-dialog-btn">
            {tier ? "Update Tier" : "Add Tier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
